/**
 * Screen-local API module for the Co-op stats report (task 007-03; task
 * 035-08 extends the row DTO and the per-row export).
 *
 * Deliberately outside `lib/api.ts` (ADR-010 gap #2 → A2 (b)): the Stage-C norm
 * is a screen-local module so the shared typed client stays untouched. Same
 * base-URL expression and `{ data }` envelope handling as `lib/api.ts`; the
 * endpoints (Lane A, tasks 007-01/02) are `GET /admin/reports/co-op-counts` and
 * `GET /admin/reports/co-op-leads.csv` (ADR-010 §8).
 *
 * The response contract is the one pinned in `tasks/007_billing_and_exports/`
 * task 01 / task 03, extended by task 035-08 (dates, limit pair, partner
 * cells). `zod` is not a dependency of this package — the style
 * union is derived from the shared runtime enum instead.
 */

import type { CampaignStatus } from '@leads-platform/shared';
import { campaignStyleEnum } from '@leads-platform/shared';
import { apiFetch } from '../../lib/api';

/** The four campaign-style enum values on the wire, derived from the shared runtime enum. */
export type CampaignStyleValue = (typeof campaignStyleEnum.options)[number];

/** Grain of the counts query (ADR-010 §8): one row per campaign or per advertiser. */
export type CoopCountsGroup = 'campaign' | 'advertiser';

/** Which row set the per-row export streams (epic 035 README decision 8 (a)). */
export type CoopLeadsVariant = 'all' | 'unique';

/** One counts row. The campaign grain carries the campaign columns; the advertiser grain nulls them. */
export interface CoopCountsRow {
  advertiserId: string;
  advertiserName: string;
  campaignId: string | null;
  campaignName: string | null;
  campaignStyle: CampaignStyleValue | null;
  campaignStatus: CampaignStatus | null;
  pricePerLead: number | null;
  startDate: string | null;
  endDate: string | null;
  limitType: 'total_clicks' | 'unique_leads' | 'unique_clicks' | null;
  limitValue: number | null;
  dailyCap: number | null;
  partnerUrl: string | null;
  partnerEndpointName: string | null;
  sent: number;
  accepted: number;
  duplicates: number;
  rejected: number;
  errors: number;
  skipped: number;
  uniqueLeads: number;
  revenue: number | null;
}

/** Column sums over the selection; `uniqueLeads` is distinct across it (non-additive). */
export interface CoopCountsTotals {
  sent: number;
  accepted: number;
  duplicates: number;
  rejected: number;
  errors: number;
  skipped: number;
  uniqueLeads: number;
  revenue: number | null;
}

export interface CoopCountsResponse {
  from: string;
  to: string;
  timezone: string;
  group: CoopCountsGroup;
  includeTest: boolean;
  rows: CoopCountsRow[];
  totals: CoopCountsTotals;
}

/**
 * Screen filter state. `''` means the filter is off and is omitted from the
 * query string; `from`/`to` (`YYYY-MM-DD`) and `includeTest` are always sent.
 */
export interface CoopCountsFilters {
  from: string;
  to: string;
  advertiserId: string;
  campaignId: string;
  campaignStyle: CampaignStyleValue | '';
  campaignStatus: CampaignStatus | '';
  includeTest: boolean;
  group: CoopCountsGroup;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/**
 * Counts query string — omits empty filter values, always carries
 * `includeTest` as `'true' | 'false'`, and keys the grain with `group`.
 */
export function buildCoopCountsQuery(filters: CoopCountsFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.advertiserId) params.set('advertiserId', filters.advertiserId);
  if (filters.campaignId) params.set('campaignId', filters.campaignId);
  if (filters.campaignStyle) params.set('campaignStyle', filters.campaignStyle);
  if (filters.campaignStatus) params.set('campaignStatus', filters.campaignStatus);
  params.set('includeTest', filters.includeTest ? 'true' : 'false');
  params.set('group', filters.group);
  return params;
}

/** Export query — the counts filters minus `group` (the CSV is a per-lead stream, grain-less), plus the variant. */
function buildCoopLeadsQuery(request: CoopLeadsExportRequest): URLSearchParams {
  const params = buildCoopCountsQuery(request);
  params.delete('group');
  params.set('variant', request.variant);
  return params;
}

/** Absolute counts URL, ready for `fetch`. */
export function coopCountsUrl(filters: CoopCountsFilters): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/co-op-counts?${buildCoopCountsQuery(filters).toString()}`;
}

/**
 * A `co-op-leads.csv` target: the screen's filters, plus the variant and — for
 * a per-row export — the row's campaign id (empty string = the screen-level
 * export, which keeps the user's campaign filter as-is).
 */
export interface CoopLeadsExportRequest extends CoopCountsFilters {
  campaignId: string;
  variant: CoopLeadsVariant;
}

/** Absolute per-lead export URL for `window.open` (the browser streams the CSV download). */
export function coopLeadsExportUrl(request: CoopLeadsExportRequest): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/co-op-leads.csv?${buildCoopLeadsQuery(request).toString()}`;
}

/**
 * `message` of a Nest error body without a cast: narrow the unknown JSON with
 * the `in` operator, then the string check. Falls back to the HTTP status text.
 */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = body.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/** Success envelope — matches the api's `ApiResponse<T>` (conventions §DTO). */
interface CoopCountsEnvelope {
  data: CoopCountsResponse;
}

/** Fetch the counts for one filter set. Non-2xx throws with the body's message (or the status text). */
export async function fetchCoopCounts(filters: CoopCountsFilters): Promise<CoopCountsResponse> {
  const response = await apiFetch(coopCountsUrl(filters));
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    throw new Error(messageFromBody(body, response.statusText));
  }
  const envelope: CoopCountsEnvelope = await response.json();
  return envelope.data;
}
